import { put, takeEvery, all } from 'redux-saga/effects';
import * as constants from '../constants/madlenIntegration';
import { Log } from 'ng2-logger';
import Translator from '../../core/i18n';
import { Notification } from '../../components/notification/main';
import * as coreActions from '../actions/core';
import * as madlenIntegrationPageAction from '../actions/madlenIntegration';
import { ICreateSendContentJob, IGetContents, IGetMadlenActivityData, IMadlenAISended } from '../../models/action/madlenIntegration';
import { MadlenIntegrationService } from '../../api/madlen-integration.service';

const T = Translator.create();
const _logger = Log.create('Survey Saga');
const _madlenIntegrationService = new MadlenIntegrationService();

function handleErrors(errorList: any) {
    if (errorList) {
        Object.keys(errorList).forEach((key: any) => {
            Notification.setNotification('error', T.t(errorList[key]), 'topRight');
        });
    } else {
        Notification.setNotification('error', T.t('r_default_error_message'), 'topRight');
    }
}

function handleSpinnerAndCallback(params: any, callbackData: any) {
    if (params.spinnerId) {
        put(coreActions.setSpinner(params.spinnerId, 'set'));
    }
    if (params.callback) {
        params.callback(callbackData);
    }
}

function logError(error: any) {
    _logger.error(error);
    Notification.setNotification('error', T.t('r_default_error_message'), 'topRight');
}

function* getActivityListForMadlen(params: IGetMadlenActivityData): any {
    try {

        const response: Response = yield _madlenIntegrationService.RequestGetActivitiesForMadlenAsync(params);

        if (response.status !== 200) {
            const errorList = yield response.json();
            handleErrors(errorList);
            handleSpinnerAndCallback(params, null);
            return;
        }

        const pageData = yield response.json();

        yield put(madlenIntegrationPageAction.SetActivityListMadlen(pageData));

        if (params.spinnerId) {
            yield put(coreActions.setSpinner(params.spinnerId, 'set'));
        }

    } catch (error: any) {
        logError(error);
    }
}

function* createSendContentJob(params: ICreateSendContentJob): any {
    try {
        const response: Response = yield _madlenIntegrationService.RequestCreateSendContentJob({ CourseId: params.courseId });

        if (response.status !== 200) {
            const errorList = yield response.json();
            handleErrors(errorList);
            handleSpinnerAndCallback(params, null);
            return;
        }

        const pageDataString = yield response.json();
        const pageData = JSON.parse(pageDataString);

        Notification.setNotification("success", pageData.successMessage, 'topRight');

        if (params.callback) {
            params.callback({ ...pageData, courseId: params.courseId });
        }

    } catch (error: any) {
        logError(error);
    }
}

function* madlenSendContentAI(params: IMadlenAISended): any {
    try {
        const response: Response = yield _madlenIntegrationService.RequestMadlenSendContentAI({
            courseId: params.CourseId,
            classId: params.ClassId,
            disAllowedIds: params.DisAllowedIds,
            isAll: params.isAll,
            activitiesWithType: params.ActivitiesWithType,
            aiSendedId: params.AISendedId,
            jobId: params.JobId,
        });

        if (response.status !== 200) {
            const errorList = yield response.json();

            if (errorList.generalError) {
                try {
                    const generalError = JSON.parse(errorList.generalError);
                    if (generalError.failedActivities) {
                        generalError.failedActivities.forEach((activity: any) => {
                            Notification.setNotification(
                                'error',
                                `${activity.errorMessage}`,
                                'topRight'
                            );
                        });
                    }
                } catch (parseError) {
                    console.error("Failed to parse generalError:", parseError);
                }
            } else {
                handleErrors(errorList);
            }

            handleSpinnerAndCallback(params, null);
            return;
        }

        const pageData = yield response.json();

        if (params.callback) {
            params.callback("success");
        }
    } catch (error: any) {
        logError(error);
    }
}


function* getContents(params: IGetContents): any {
    try {
        const response: Response = yield _madlenIntegrationService.RequestGetAIContentsAsync(params);

        if (response.status !== 200) {
            const errorList = yield response.json();
            handleErrors(errorList);
            handleSpinnerAndCallback(params, null);
            return;
        }

        const pageData = yield response.json();
        yield put(madlenIntegrationPageAction.SetContentListMadlen(pageData.results, pageData.filters, params.Status ?? 0));

        if (params.spinnerId) {
            yield put(coreActions.setSpinner(params.spinnerId, 'set'));
        }

    } catch (error: any) {
        logError(error);
    }
}

function* getMadlenUrl(params:any): any {
    try {
        const response: Response = yield _madlenIntegrationService.RequestGetMadlenUrlAsync();

        if (response.status !== 200) {
            const errorList = yield response.json();
            handleErrors(errorList);
            handleSpinnerAndCallback(params, null);
            return;
        }

        const pageData = yield response.json();
        yield put(madlenIntegrationPageAction.SetMadlenUrl(pageData.deeplink));

        if (params.spinnerId) {
            yield put(coreActions.setSpinner(params.spinnerId, 'set'));
        }

    } catch (error: any) {
        logError(error);
    }
}

export function* watchGetActivityListForMadlen() {
    yield takeEvery(constants.actions.MADLEN_INTEGRATION_GET_LIST, getActivityListForMadlen);
    return null;
}

export function* watchCreateSendContentJob() {
    yield takeEvery(constants.actions.MADLEN_INTEGRATION_CREATE_SEND_CONTENT_JOB, createSendContentJob);
    return null;
}

export function* watchMadlenSendContentAI() {
    yield takeEvery(constants.actions.MADLEN_SEND_CONTENT, madlenSendContentAI);
    return null;
}

export function* watchGetContents() {
    yield takeEvery(constants.actions.MADLEN_INTEGRATION_GET_CONTENTS, getContents);
    return null;
}

export function* watchGetMadlenUrl() {
    yield takeEvery(constants.actions.MADLEN_INTEGRATION_GET_MADLEN_URL, getMadlenUrl);
    return null;
}

export function* MadlenIntegrationSagas() {
    yield all([
        watchGetActivityListForMadlen(),
        watchCreateSendContentJob(),
        watchMadlenSendContentAI(),
        watchGetContents(),
        watchGetMadlenUrl(),
    ]);
}
